import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Download, Loader2, AlertCircle, Check, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { exportData } from '@/lib/api';
import { cn } from '@/lib/utils';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: 'candidates' | 'jobs' | 'applications';
}

interface FieldOption {
  key: string;
  label: string;
}

const CANDIDATE_FIELDS: FieldOption[] = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'fullName', label: 'Full Name' },
  { key: 'email', label: 'Email' },
  { key: 'whatsappPhone', label: 'WhatsApp Phone' },
  { key: 'phoneSecondary', label: 'Secondary Phone' },
  { key: 'currentLocation', label: 'Current Location' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'yearsExperience', label: 'Years of Experience' },
  { key: 'educationLevel', label: 'Education Level' },
  { key: 'currentJobTitle', label: 'Current Job Title' },
  { key: 'currentEmployer', label: 'Current Employer' },
  { key: 'skills', label: 'Skills' },
  { key: 'preferredJobTypes', label: 'Preferred Job Types' },
  { key: 'expectedSalaryRange', label: 'Expected Salary Range' },
  { key: 'availabilityDate', label: 'Availability Date' },
  { key: 'linkedinUrl', label: 'LinkedIn URL' },
  { key: 'source', label: 'Source' },
  { key: 'onboardingStatus', label: 'Onboarding Status' },
  { key: 'createdAt', label: 'Created At' },
  { key: 'tags', label: 'Tags' },
];

const JOB_FIELDS: FieldOption[] = [
  { key: 'title', label: 'Title' },
  { key: 'company', label: 'Company' },
  { key: 'location', label: 'Location' },
  { key: 'jobType', label: 'Job Type' },
  { key: 'remoteType', label: 'Remote Type' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'requiredSkills', label: 'Required Skills' },
  { key: 'experienceMin', label: 'Min Experience' },
  { key: 'experienceMax', label: 'Max Experience' },
  { key: 'salaryMin', label: 'Salary Min' },
  { key: 'salaryMax', label: 'Salary Max' },
  { key: 'salaryCurrency', label: 'Salary Currency' },
  { key: 'positionsAvailable', label: 'Positions Available' },
  { key: 'deadline', label: 'Deadline' },
  { key: 'applicationCount', label: 'Applications' },
  { key: 'createdAt', label: 'Created At' },
];

const APPLICATION_FIELDS: FieldOption[] = [
  { key: 'candidateFirstName', label: 'First Name' },
  { key: 'candidateLastName', label: 'Last Name' },
  { key: 'candidateEmail', label: 'Email' },
  { key: 'candidatePhone', label: 'WhatsApp Phone' },
  { key: 'candidateLocation', label: 'Location' },
  { key: 'candidateSkills', label: 'Skills' },
  { key: 'currentStage', label: 'Pipeline Stage' },
  { key: 'notes', label: 'Notes' },
  { key: 'disqualificationReason', label: 'Disqualification Reason' },
  { key: 'createdAt', label: 'Applied At' },
  { key: 'updatedAt', label: 'Last Updated' },
];

const ENTITY_LABELS: Record<string, string> = {
  candidates: 'Candidates',
  jobs: 'Jobs',
  applications: 'Applications',
};

function getFieldsForEntity(entity: string): FieldOption[] {
  switch (entity) {
    case 'candidates':
      return CANDIDATE_FIELDS;
    case 'jobs':
      return JOB_FIELDS;
    case 'applications':
      return APPLICATION_FIELDS;
    default:
      return [];
  }
}

export function ExportDialog({ open, onOpenChange, entity }: ExportDialogProps) {
  const fields = getFieldsForEntity(entity);
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');
  const [selectedFields, setSelectedFields] = useState<string[]>(
    fields.map((f) => f.key),
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => exportData(entity, format, selectedFields),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `${entity}-export-${timestamp}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      onOpenChange(false);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Export failed');
    },
  });

  const toggleField = (key: string) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key],
    );
  };

  const toggleAll = () => {
    if (selectedFields.length === fields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields(fields.map((f) => f.key));
    }
  };

  const handleSubmit = () => {
    if (selectedFields.length === 0) {
      setError('Please select at least one field to export');
      return;
    }
    setError(null);
    mutation.mutate();
  };

  const handleClose = (value: boolean) => {
    if (!mutation.isPending) {
      setError(null);
      onOpenChange(value);
    }
  };

  const allSelected = selectedFields.length === fields.length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export {ENTITY_LABELS[entity]}</DialogTitle>
          <DialogDescription>
            Choose a format and select which fields to include in the export.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Format Selection */}
          <div className="space-y-2">
            <Label>Format</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormat('csv')}
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                  format === 'csv'
                    ? 'border-emerald-500 bg-emerald-600/10'
                    : 'border-white/10 bg-bg-secondary hover:bg-white/5'
                )}
              >
                <FileText
                  className={cn(
                    'h-5 w-5',
                    format === 'csv' ? 'text-emerald-400' : 'text-text-tertiary',
                  )}
                />
                <div>
                  <p className={cn(
                    'text-sm font-medium',
                    format === 'csv' ? 'text-text-primary' : 'text-text-secondary',
                  )}>
                    CSV
                  </p>
                  <p className="text-xs text-text-tertiary">
                    Comma-separated values
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setFormat('xlsx')}
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                  format === 'xlsx'
                    ? 'border-emerald-500 bg-emerald-600/10'
                    : 'border-white/10 bg-bg-secondary hover:bg-white/5'
                )}
              >
                <FileSpreadsheet
                  className={cn(
                    'h-5 w-5',
                    format === 'xlsx' ? 'text-emerald-400' : 'text-text-tertiary',
                  )}
                />
                <div>
                  <p className={cn(
                    'text-sm font-medium',
                    format === 'xlsx' ? 'text-text-primary' : 'text-text-secondary',
                  )}>
                    XLSX
                  </p>
                  <p className="text-xs text-text-tertiary">
                    Excel spreadsheet
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* Field Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Fields</Label>
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="max-h-52 overflow-y-auto space-y-1 rounded-md border border-white/10 bg-bg-secondary p-2">
              {fields.map((field) => {
                const isSelected = selectedFields.includes(field.key);
                return (
                  <button
                    key={field.key}
                    type="button"
                    onClick={() => toggleField(field.key)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                      isSelected
                        ? 'bg-emerald-600/20 text-text-primary'
                        : 'hover:bg-white/5 text-text-secondary'
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded border transition-colors flex-shrink-0',
                        isSelected
                          ? 'bg-emerald-600 border-emerald-600'
                          : 'border-white/20 bg-transparent'
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span>{field.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-text-tertiary">
              {selectedFields.length} of {fields.length} fields selected
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedFields.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-1.5" />
                Export {format.toUpperCase()}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
